import { Component, OnInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { LocalStorageService } from '../local-storage.service';
import { TranslationService } from '../translation.service';
import { ThemeService } from '../service/theme.service';
import { filter } from 'rxjs/operators'
import { VersionHistoryService } from '../service/version-history.service';

@Component({
    selector: 'app-footer',
    templateUrl: './footer.component.html',
    styleUrls: ['./footer.component.css'],
    standalone: false
})
export class FooterComponent implements OnInit {

  /* Language state */
  selectedLanguage: string = "fr";
  initLang: string = this.localStorageService.getItem('language') ?? 'fr';
  public languages = [
    { label: 'FR', value: 'fr' },
    { label: 'EN', value: 'en' },
    { label: 'ES', value: 'es' }
  ];

  /* UI flags */
  public currentYear: number = 0;
  options: string[] = ['FR', 'EN', 'ES'];
  isOpen: boolean = false;
  public isMouseInteraction: boolean = false;
  public isSelectOpen: boolean = false;
  public isMobile: boolean = false;

  /* Page flags */
  public isLoginPage: boolean = false;
  public isManagePage: boolean = false;

  constructor(
    private localStorageService: LocalStorageService,
    private translate: TranslationService,
    public theme: ThemeService,
    private router: Router,
    private versionHistory: VersionHistoryService
  ) {
    // check the language defined from localstorage and insert as current option in Select Language Menu
    switch(this.initLang) {
      case 'fr':
        this.selectedLanguage = 'fr'; // french
        break;
      case 'en':
        this.selectedLanguage = 'en'; // english
        break;
      case 'es':
        this.selectedLanguage = 'es'; // espanish
        break;
      default:
        this.selectedLanguage = 'fr'; // french
        break;
    }
  }

  /**
   * Initialize footer state: detect device, set current year, flag page type, subscribe to route changes, and load language.
   */
  ngOnInit(): void{
    // This const will take true if there's a device detected that is not a desktop
    this.isMobile = /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    this.currentYear = new Date().getFullYear();
    this.theme.IsLoginPageActive();
    this.updatePageFlags(this.router.url);
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.updatePageFlags(event.urlAfterRedirects);
      });
    this.initializeLanguageSettings();
  }



  /**
   * Apply the selected language: update translate service, persist to localStorage, set <html lang>, and notify listeners.
   */
  async triggerLanguageChange() {
    // Call the translateTo() method of the translate service
    // and pass the selectedLanguage as an argument
    this.translate.translateTo(this.selectedLanguage);

    // Add the selectedLanguage to the 'language' item in the localStorage
    this.localStorageService.addItem('language', this.selectedLanguage);

    // Update the initial language to the selectedLanguage
    this.initLang = this.selectedLanguage;

    // Dynamically update the <html> lang attribute
    const htmlElement = document.documentElement; // Access the <html> element
    htmlElement.lang = this.selectedLanguage; // Set the lang attribute

    this.translate.selectLangageChange();
  }

  /**
   * Initialize language from storage (fallback to 'fr'), set <html lang>, and load translations.
   */
  initializeLanguageSettings() {
    // Retrieve language from localStorage or fallback to a default
    this.selectedLanguage = this.localStorageService.getItem('language') ?? 'fr';
    this.initLang = this.selectedLanguage;

    // Set the <html> lang attribute
    const htmlElement = document.documentElement;
    htmlElement.lang = this.selectedLanguage;

    // Optionally, trigger translation based on the stored language
    this.translate.translateTo(this.selectedLanguage);
  }

  /** Toggle language dropdown open/closed. */
  toggleDropdown() {
    this.isOpen = !this.isOpen;
  }

  /**
   * Select a language option from custom dropdown and trigger change.
   * @param option language code (e.g., 'fr', 'en', 'es')
   */
  selectOption(option: string) {
    this.selectedLanguage = option.toLowerCase();
    this.isOpen = false;
    this.onLanguageChange();
  }

   /**
    * Handle mouse interaction on native select; commit change when closing.
    * @param event native click event
    */
   onMouseInteraction(event: Event): void {
    this.isMouseInteraction = true;
    if (this.isSelectOpen) {
      this.onLanguageChange();
      this.isSelectOpen = false;
    }
    else{
      this.isSelectOpen = true;
    }
  }

  /**
   * Handle keyboard interaction (Enter to open/confirm) on native select.
   * @param event keyboard event from select
   */
  onKeyboardInteraction(event: KeyboardEvent): void {
    this.isMouseInteraction = true;
    if (this.isSelectOpen) {
      if (event.key === 'Enter') {
        this.onLanguageChange();
        this.isSelectOpen = false;
      }
    }
    else{
      if (event.key === 'Enter') {
        this.isSelectOpen = true;
      }
    }
  }

  /**
   * Commit language change only when triggered by user interaction to avoid double firing.
   */
  onLanguageChange(): void {
    if (this.isMouseInteraction) {
      this.triggerLanguageChange();
      this.isMouseInteraction = false;
    }
  }

  /**
   * Set page flags based on URL path to adjust footer layout.
   * @param url current URL
   */
  private updatePageFlags(url: string): void {
    const path = url.split('?')[0];
    this.isLoginPage = path.startsWith('/login')
      || path.startsWith('/activate')
      || path.startsWith('/sign-up-mfa')
      || path.startsWith('/change-password&redirect=me');

    this.isManagePage = path.startsWith('/manage');
  }

  /** Open the version history overlay. */
  openVersionHistory(): void {
    this.versionHistory.open();
  }
}
